import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import PlateSearchConfigurator, { type SearchData } from '@/components/plate-search/PlateSearchConfigurator'
import { UmweltplaketteModal, CarbonUpgradeModal } from '@/components/plate-search/UpsellModal'
import AccessoriesSection from '@/components/shop/AccessoriesSection'
import GiftPlateSection from '@/components/shop/GiftPlateSection'
import ShopFlyerGallery from '@/components/shop/ShopFlyerGallery'
import { useCartStore } from '@/store/cartStore'
import toast from 'react-hot-toast'

type ShopCategory =
  | null // Show main cards
  | 'standard-plates'
  | 'custom-plates'
  | 'gift-plates'
  | 'accessories'

interface ShopCard {
  id: ShopCategory
  label: string
  icon: string
  description: string
  active: boolean
}

const SHOP_CARDS: ShopCard[] = [
  {
    id: 'standard-plates',
    label: 'Standard-Kennzeichen',
    icon: '🚗',
    description: 'Bestehende Kennzeichen – Verfügbarkeit prüfen',
    active: true,
  },
  {
    id: 'custom-plates',
    label: 'Individuelle Kennzeichen',
    icon: '🎨',
    description: 'Gestalten Sie Ihr eigenes Kennzeichen',
    active: true,
  },
  {
    id: 'gift-plates',
    label: 'Geschenkkennzeichen',
    icon: '🎁',
    description: 'Demnächst verfügbar',
    active: false,
  },
  {
    id: 'accessories',
    label: 'Zubehör',
    icon: '🛠️',
    description: 'Fahrzeugzubehör und Zulassungsartikel',
    active: true,
  },
]

const Shop = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const addItem = useCartStore(s => s.addItem)
  const [activeCategory, setActiveCategory] = useState<ShopCategory>(null)

  // Upsell flow for direct plate (custom-plates)
  const [directPlateData, setDirectPlateData] = useState<SearchData | null>(null)
  const [upsellStep, setUpsellStep] = useState<0 | 1 | 2>(0)

  const directPlateStr = directPlateData
    ? `${directPlateData.cityCode} ${directPlateData.letters} ${directPlateData.numbers}`
    : ''

  const handleDirectAddToCart = (data: SearchData) => {
    setDirectPlateData(data)
    setUpsellStep(1) // Start upsell: Umweltplakette first
  }

  const finishDirectAdd = () => {
    if (!directPlateData) return
    addItem({
      productId: `plate-${directPlateData.cityCode}-${directPlateData.letters}-${directPlateData.numbers}-${Date.now()}`,
      productName: `Kennzeichen ${directPlateStr}`,
      price: 20.00,
      quantity: 1,
      customization: {
        cityCode: directPlateData.cityCode,
        letters: directPlateData.letters,
        numbers: directPlateData.numbers,
        plateSize: directPlateData.plateSize,
        plateType: directPlateData.plateType,
        vehicleCategory: directPlateData.vehicleCategory,
      },
    })
    toast.success(`Kennzeichen ${directPlateStr} zum Warenkorb hinzugefügt!`)
    setDirectPlateData(null)
    setUpsellStep(0)
  }

  const handleUmweltAccept = () => {
    addItem({
      productId: `umweltplakette-${directPlateStr}`,
      productName: `Umweltplakette (${directPlateStr})`,
      price: 5.00,
      quantity: 1,
    })
    toast.success('Umweltplakette hinzugefügt!')
    setUpsellStep(2)
  }

  const handleUmweltDecline = () => setUpsellStep(2)

  const handleCarbonAccept = () => {
    addItem({
      productId: `carbon-upgrade-${directPlateStr}`,
      productName: `Carbon Upgrade (${directPlateStr})`,
      price: 4.90,
      quantity: 1,
    })
    toast.success('Carbon Upgrade hinzugefügt!')
    finishDirectAdd()
  }

  const handleCarbonDecline = () => finishDirectAdd()

  const handleCardClick = (card: ShopCard) => {
    if (!card.active) {
      toast.error('Diese Funktion ist noch nicht verfügbar')
      return
    }
    if (card.id === 'standard-plates') {
      navigate('/wunschkennzeichen')
      return
    }
    setActiveCategory(card.id)
  }

  const handleBack = () => {
    setActiveCategory(null)
  }

  const getCategoryContent = () => {
    if (activeCategory === 'accessories') {
      return <AccessoriesSection />
    }

    if (activeCategory === 'gift-plates') {
      return <GiftPlateSection />
    }

    if (activeCategory === 'custom-plates') {
      return (
        <div className="space-y-6">
          <div className="text-center mb-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-gradient mb-2">
              Individuelle Kennzeichen
            </h2>
            <p className="text-gray-400 text-sm sm:text-base">
              Gestalten Sie Ihr Wunschkennzeichen (ohne Reservierung)
            </p>
          </div>
          <PlateSearchConfigurator
            onSearch={handleDirectAddToCart}
            mode="direct"
          />
        </div>
      )
    }

    return null
  }

  return (
    <div className="bg-dark-950 min-h-screen py-6 sm:py-8 relative overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 bg-grid-pattern opacity-20"></div>
      <div className="absolute inset-0 bg-gradient-mesh opacity-30"></div>

      <div className="container-custom px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-6 sm:mb-8"
        >
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-3 sm:mb-4">
              <span className="text-gradient bg-clip-text text-transparent bg-gradient-to-r from-primary-400 via-purple-400 to-primary-400">
                {t('shop.title')}
              </span>
            </h1>
            <p className="text-base sm:text-lg text-gray-400">
              {t('shop.subtitle')}
            </p>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-primary-500 to-transparent mt-4"></div>
          </div>
        </motion.div>

        {/* Main Shop Cards or Category Content */}
        <AnimatePresence mode="wait">
          {activeCategory === null ? (
            <motion.div
              key="shop-home"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="w-full"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-16">
                {SHOP_CARDS.map((card, index) => (
                  <motion.button
                    key={card.id}
                    onClick={() => handleCardClick(card)}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={!card.active}
                    className={`
                    relative p-8 sm:p-10 bg-dark-900 rounded-2xl border-2 transition-all text-left
                    ${card.active
                        ? 'border-gray-700 hover:border-primary-500/50 cursor-pointer'
                        : 'border-gray-800 opacity-60 cursor-not-allowed'
                      }
                  `}
                  >
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                      <div className={`
                      p-4 sm:p-5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl text-white shadow-lg shadow-primary-500/50 text-4xl sm:text-5xl
                      ${card.active ? 'group-hover:scale-110 transition-transform' : ''}
                    `}>
                        {card.icon}
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3">
                          {card.label}
                        </h2>
                        <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
                          {card.description}
                        </p>
                        {!card.active && (
                          <span className="inline-block mt-3 px-3 py-1 bg-gray-800 text-gray-500 text-xs font-semibold rounded-full">
                            Bald verfügbar
                          </span>
                        )}
                      </div>
                      {card.active && (
                        <svg
                          className="w-6 h-6 text-primary-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      )}
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Flyer Gallery - Displaying multiple vertical flyers */}
              <ShopFlyerGallery
                title={t('shop.flyerGalleryTitle', 'Entdecken Sie unsere Angebote')}
                images={[
                  '/assets/KP24 flyer/IMG_4167.PNG',
                  '/assets/KP24 flyer/IMG_4168.PNG',
                  '/assets/KP24 flyer/IMG_4170.PNG',
                  '/assets/KP24 flyer/IMG_4171.PNG'
                ]}
              />
            </motion.div>
          ) : (
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              {/* Back Button */}
              <motion.button
                onClick={handleBack}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 text-gray-400 hover:text-primary-400 transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                <span className="font-semibold">Zurück zur Übersicht</span>
              </motion.button>

              {/* Category Content */}
              {getCategoryContent()}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Upsell Modals (for direct/individual plates) */}
      {upsellStep === 1 && (
        <UmweltplaketteModal
          plateNumber={directPlateStr}
          onAccept={handleUmweltAccept}
          onDecline={handleUmweltDecline}
        />
      )}
      {upsellStep === 2 && directPlateData && (
        <CarbonUpgradeModal
          plateNumber={directPlateStr}
          onAccept={handleCarbonAccept}
          onDecline={handleCarbonDecline}
          cityCode={directPlateData.cityCode}
          letters={directPlateData.letters}
          numbers={directPlateData.numbers}
          plateSize={directPlateData.plateSize}
          plateType={directPlateData.plateType}
          vehicleCategory={directPlateData.vehicleCategory}
        />
      )}
    </div>
  )
}

export default Shop
